import { computed, Signal } from '@angular/core';
import {
  DashboardPageKey,
  LENDEE_DASHBOARD_CARD_CONFIG,
  LENDEE_ANALYTICS_CARD_CONFIG,
  COOL_CREDIT_DASHBOARD_CARD_CONFIG,
  COOL_CREDIT_GROWTH_CARD_CONFIG,
  DAILY_CRON_CARD_CONFIG,
  DAILY_CRON_BACKEND_WIDGET_KEYS,
  OVERDUE_CRON_CARD_CONFIG,
  OVERDUE_CRON_BACKEND_WIDGET_KEYS,
  ARRANGEMENT_CRON_OVERVIEW_CARD_CONFIG,
  ARRANGEMENT_CRON_OVERVIEW_BACKEND_WIDGET_KEYS,
  ARRANGEMENT_CRON_TRENDS_CARD_CONFIG,
  ARRANGEMENT_CRON_TRENDS_BACKEND_WIDGET_KEYS,
  ARRANGEMENT_CRON_ADMIN_PERFORMANCE_CARD_CONFIG,
  ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_WIDGET_KEYS,
  LendeeDashboardSectionId,
  LendeeAnalyticsSectionId,
  CoolCreditDashboardSectionId,
  CoolCreditGrowthSectionId,
  DailyCronSectionId,
  OverdueCronSectionId,
  ArrangementCronOverviewSectionId,
  ArrangementCronTrendsSectionId,
  ArrangementCronAdminPerformanceSectionId,
  type DashboardCardConfig,
} from '../../../core/enums/dashboard-cards.enum';

/** Substrings matched against catalog `page.name` (lowercase). */
export const CATALOG_PAGE_HINTS: Partial<Record<DashboardPageKey, string[]>> = {
  [DashboardPageKey.SOCIAL_PLATFORM]: ['social media performance', 'social platform'],
  [DashboardPageKey.SEO_STATS]: ['seo stats'],
  [DashboardPageKey.SEO_OVERVIEW]: ['ads overview', 'seo overview'],
  [DashboardPageKey.CAMPAIGN_ADS_STATS]: [
    'campaign metrics',
    'campaign-metrics',
    'campaign ads',
    'campaign ad',
  ],
  [DashboardPageKey.INDUSTRY_CATEGORIZATION]: ['industry', 'categorization'],
  [DashboardPageKey.TRAINING_DASHBOARD]: ['training and demo', 'training dashboard'],
  [DashboardPageKey.PRACTINA_DASHBOARD]: [
    'analytical-dashboard-widgets',
    'analytical dashboard widgets',
  ],
  [DashboardPageKey.LENDEE_ANALYTICS]: [
    'analytical-dashboard',
    'analytical dashboard',
    'lendee analytics',
  ],
  [DashboardPageKey.LENDEE_DASHBOARD]: [
    'lendee dashboard',
    'dashboard',
  ],
  [DashboardPageKey.COOL_CREDIT_DASHBOARD]: [
    'analytical-dashboard',
    'analytical dashboard',
    'cool credit dashboard',
    'coolcredit dashboard',
    'cool credit',
  ],
  [DashboardPageKey.GROWTH_ACTIVATION_REVENUE]: [
    'growth-activation-and-revenue',
    'cool-credit-growth-revenue',
    'growth, activation',
    'growth activation',
    'growth activation & revenue',
  ],
  [DashboardPageKey.BUSINESS_PERFORMANCE_REPORT]: [
    'business performance',
    'visionboard-business',
    'business-performance-report',
    'business performance report',
  ],
  [DashboardPageKey.CONTRACTS_REPORT]: [
    'visionboard-contracts',
    'contracts-report',
    'contracts report',
    'contract report',
    'contracts',
  ],
  [DashboardPageKey.DAILY_CRON_PAYMENTS]: [
    'daily cron payments',
    'daily-cron-payments',
    'daily cron',
    'try again cron',
  ],
  [DashboardPageKey.OVERDUE_CRON_PAYMENTS]: [
    'overdue cron payments',
    'overdue-cron-payments',
    'overdue cron',
    'friday cron',
  ],
  [DashboardPageKey.ARRANGEMENT_CRON_PAYMENTS]: [
    'arrangement cron payments',
    'arrangement-cron-payments',
    'arrangement cron',
    'arrangement analytics',
    "arrangement's analytics",
  ],
  [DashboardPageKey.ARRANGEMENT_CRON_OVERVIEW]: [
    'arrangement cron - overview table',
    'arrangement-cron-overview-table',
    'arrangement cron overview',
  ],
  [DashboardPageKey.ARRANGEMENT_CRON_TRENDS]: [
    'arrangement cron trends and recovery',
    'arrangement-cron-trends-and-recovery',
    'trends and recovery',
  ],
  [DashboardPageKey.ARRANGEMENT_CRON_ADMIN_PERFORMANCE]: [
    'arrangement cron admin performance',
    'arrangement-cron-admin-performance',
    'admin performance',
  ],
};

/**
 * Exact `page.slug` matches (lowercase equality). Used when the backend
 * registers a short slug like `business` (pages.id = 41) that would be
 * too broad as a substring hint against `page.name`.
 */
export const CATALOG_PAGE_SLUG_HINTS: Partial<Record<DashboardPageKey, string[]>> = {
  [DashboardPageKey.BUSINESS_PERFORMANCE_REPORT]: ['business'],
  [DashboardPageKey.ARRANGEMENT_CRON_OVERVIEW]: ['arrangement-cron-overview-table'],
  [DashboardPageKey.ARRANGEMENT_CRON_TRENDS]: ['arrangement-cron-trends-and-recovery'],
  [DashboardPageKey.ARRANGEMENT_CRON_ADMIN_PERFORMANCE]: ['arrangement-cron-admin-performance'],
};

/**
 * Fallback resolver keyed on `widgets[].category` when `page.name` /
 * `page.slug` do not contain any {@link CATALOG_PAGE_HINTS} substring.
 * Denefits registers Business Performance as `name: "Business" /
 * slug: "business"` (page id 41) with widgets under
 * `category: "visionboard-business"`.
 */
export const CATALOG_PAGE_WIDGET_CATEGORY_HINTS: Partial<
  Record<DashboardPageKey, string[]>
> = {
  [DashboardPageKey.BUSINESS_PERFORMANCE_REPORT]: ['visionboard-business'],
  [DashboardPageKey.CONTRACTS_REPORT]: ['visionboard-contracts'],
  [DashboardPageKey.GROWTH_ACTIVATION_REVENUE]: ['cool-credit-growth-revenue'],
  [DashboardPageKey.DAILY_CRON_PAYMENTS]: ['daily-cron-payments'],
  [DashboardPageKey.OVERDUE_CRON_PAYMENTS]: ['overdue-cron-payments'],
};

export function resolveActiveCatalogProductId(api: {
  selectedProductPid: () => string | number | null;
  selectedProductId: () => string | null;
  products: () => any[];
}): string | number | null {
  const pid = api.selectedProductPid();
  if (pid != null && /^\d+$/.test(String(pid))) return pid;

  const selectedId = api.selectedProductId();
  if (selectedId == null) return null;

  const product = (api.products() ?? []).find(
    (p: any) => String(p?.id) === String(selectedId),
  );
  if (product?.pid != null) return product.pid;
  return selectedId;
}

export function findCatalogPageId(
  catalog: any[],
  pageKey: DashboardPageKey,
  productId?: string | number | null,
): number | null {
  const hints = CATALOG_PAGE_HINTS[pageKey] ?? [];
  const slugHints = CATALOG_PAGE_SLUG_HINTS[pageKey] ?? [];
  const categoryHints = CATALOG_PAGE_WIDGET_CATEGORY_HINTS[pageKey] ?? [];
  if (!hints.length && !slugHints.length && !categoryHints.length) return null;

  const all = (catalog ?? []) as any[];
  const scoped =
    productId != null && String(productId).trim() !== ''
      ? all.filter((p: any) => String(p?.id) === String(productId))
      : all;

  const fromScoped = resolveCatalogPageIdInProducts(scoped, hints, slugHints, categoryHints);
  if (fromScoped != null) return fromScoped;

  if (scoped !== all) {
    const fromAll = resolveCatalogPageIdInProducts(all, hints, slugHints, categoryHints);
    if (fromAll != null) {
      // eslint-disable-next-line no-console
      console.warn(
        `[findCatalogPageId] page "${pageKey}" not found under active ` +
        `product (id=${productId}); falling back to cross-product ` +
        `catalog match (pageId=${fromAll}). If this isn't the correct ` +
        `product's page, verify the catalog registration.`,
      );
      return fromAll;
    }
  }

  return null;
}

function resolveCatalogPageIdInProducts(
  products: any[],
  hints: string[],
  slugHints: string[],
  categoryHints: string[],
): number | null {
  return (
    resolveFirstMatchingPageId(products, hints) ??
    resolveFirstMatchingPageIdByExactSlug(products, slugHints) ??
    resolveFirstMatchingPageIdByWidgetCategory(products, categoryHints)
  );
}

function resolveFirstMatchingPageIdByExactSlug(
  products: any[],
  slugHints: string[],
): number | null {
  if (!slugHints.length) return null;
  for (const product of products ?? []) {
    for (const page of product?.pages ?? []) {
      const slug = String(page?.slug ?? '').toLowerCase();
      if (slugHints.some((h) => slug === h)) {
        const id = Number(page.id);
        if (Number.isFinite(id) && id > 0) return id;
      }
    }
  }
  return null;
}

function resolveFirstMatchingPageIdByWidgetCategory(
  products: any[],
  categoryHints: string[],
): number | null {
  if (!categoryHints.length) return null;
  for (const product of products ?? []) {
    for (const page of product?.pages ?? []) {
      const widgets = page?.widgets ?? [];
      const matchesCategory = widgets.some((w: any) => {
        const category = String(w?.category ?? '').toLowerCase();
        return categoryHints.some(
          (h) => category === h || category.includes(h),
        );
      });
      if (matchesCategory) {
        const id = Number(page.id);
        if (Number.isFinite(id) && id > 0) return id;
      }
    }
  }
  return null;
}

function resolveFirstMatchingPageId(
  products: any[],
  hints: string[],
): number | null {
  for (const product of products ?? []) {
    for (const page of product?.pages ?? []) {
      const name = String(page?.name ?? '').toLowerCase();
      const slug = String(page?.slug ?? '').toLowerCase();
      if (hints.some((h) => name.includes(h) || slug.includes(h))) {
        const id = Number(page.id);
        if (Number.isFinite(id) && id > 0) return id;
      }
    }
  }
  return null;
}

export function createEmbeddedSectionFilter<T extends { id: string }>(
  orderedVisibleSections: Signal<T[]>,
  mixedMode: Signal<boolean>,
  visibleWidgetKeys: Signal<string[] | null | undefined>,
  orderedSections?: Signal<T[]>,
): Signal<T[]> {
  return computed(() => {
    if (!mixedMode()) return orderedVisibleSections();

    const keys = visibleWidgetKeys();
    if (!keys?.length) return orderedVisibleSections();

    const allowed = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      allowed.add(k);
      const canonical = stripProductPrefix(k);
      if (canonical !== k) allowed.add(canonical);
    }

    const all = orderedSections ? orderedSections() : orderedVisibleSections();
    const filtered = all.filter(
      (s) => allowed.has(s.id) || allowed.has(stripProductPrefix(s.id)),
    );
    // In mixedMode (embedded in My Dashboard), return the filtered result
    // directly — even if empty. Falling back to `all` when the requested
    // section is hidden on the source page causes every other visible
    // section to appear inside this tile, producing duplicate cards.
    // Outside of mixedMode `all` is already returned at line 279, so this
    // branch is always executing with mixedMode === true.
    return filtered;
  });
}

export function stripProductPrefix(widgetKey: string): string {
  const match = /^[a-z]{2}_(.+)$/i.exec(widgetKey);
  return match ? match[1] : widgetKey;
}

/** Backend catalog `widget_key` → frontend section id (canonical, no product prefix). */
const LENDEE_DASHBOARD_BACKEND_KEYS: Partial<Record<LendeeDashboardSectionId, string>> = {
  [LendeeDashboardSectionId.REQUESTS_CARD]: 'le_ln_applications_pipeline',
  [LendeeDashboardSectionId.OFFERS_CARD]: 'le_ln_offers_responses',
  [LendeeDashboardSectionId.PAYMENT_PLANS_CARD]: 'le_ln_payment_plans',
  [LendeeDashboardSectionId.RISK_ASSESSMENT]: 'le_ln_risk_matrix',
  [LendeeDashboardSectionId.OPERATIONAL_EFFICIENCY]: 'le_ln_operational_efficiency',
  [LendeeDashboardSectionId.KEY_PERFORMANCE_METRICS]: 'le_ln_kpi_metrics',
};

const LENDEE_ANALYTICS_BACKEND_KEYS: Partial<Record<LendeeAnalyticsSectionId, string>> = {
  [LendeeAnalyticsSectionId.METRICS_OVERVIEW]: 'le_ln_operational_kpis',
  [LendeeAnalyticsSectionId.KEY_INSIGHTS]: 'le_ln_key_insights',
  [LendeeAnalyticsSectionId.RECIPIENT_FUNNEL]: 'le_ln_recipient_conversion_funnel',
  [LendeeAnalyticsSectionId.REQUEST_OFFER_PERFORMANCE]: 'le_ln_request_offer_performance',
  [LendeeAnalyticsSectionId.SIGNUPS_TREND]: 'le_ln_signups_trend',
  [LendeeAnalyticsSectionId.REQUEST_STATUS]: 'le_ln_request_status_summary',
  [LendeeAnalyticsSectionId.OFFER_OUTCOME]: 'le_ln_offer_outcome_analysis',
  [LendeeAnalyticsSectionId.OVERDUE_COLLECTIONS]: 'le_ln_overdue_collections_progress',
  [LendeeAnalyticsSectionId.PARTIAL_REPAYMENTS]: 'le_ln_partial_repayments_trend',
  [LendeeAnalyticsSectionId.RECEIVABLES_VS_REVENUE]: 'le_ln_receivables_completion_revenue',
  [LendeeAnalyticsSectionId.RECEIVABLES_RECOVERY]: 'le_ln_receivables_recovery',
  [LendeeAnalyticsSectionId.DECLINE_REASONS]: 'le_ln_top_decline_reasons',
};

const COOL_CREDIT_DASHBOARD_BACKEND_KEYS: Partial<Record<CoolCreditDashboardSectionId, string>> = {
  [CoolCreditDashboardSectionId.BUSINESS_GROWTH]: 'co_cc_business_growth',
  [CoolCreditDashboardSectionId.ACTIVATION_AND_CALLS]: 'co_cc_activation_calls',
  [CoolCreditDashboardSectionId.PRODUCT_USAGE]: 'co_cc_product_usage',
  [CoolCreditDashboardSectionId.MONETIZATION_REVENUE]: 'co_cc_monetization_revenue',
  [CoolCreditDashboardSectionId.PLAN_LIFECYCLE]: 'co_cc_plan_lifecycle',
  [CoolCreditDashboardSectionId.DISPUTES]: 'co_cc_disputes',
};

const DAILY_CRON_BACKEND_KEYS: Partial<Record<DailyCronSectionId, string>> =
  DAILY_CRON_BACKEND_WIDGET_KEYS;

const OVERDUE_CRON_BACKEND_KEYS: Partial<Record<OverdueCronSectionId, string>> =
  OVERDUE_CRON_BACKEND_WIDGET_KEYS;

const ARRANGEMENT_CRON_OVERVIEW_BACKEND_KEYS: Partial<
  Record<ArrangementCronOverviewSectionId, string>
> = ARRANGEMENT_CRON_OVERVIEW_BACKEND_WIDGET_KEYS;

const ARRANGEMENT_CRON_TRENDS_BACKEND_KEYS: Partial<
  Record<ArrangementCronTrendsSectionId, string>
> = ARRANGEMENT_CRON_TRENDS_BACKEND_WIDGET_KEYS;

const ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_KEYS: Partial<
  Record<ArrangementCronAdminPerformanceSectionId, string>
> = ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_WIDGET_KEYS;

const COOL_CREDIT_GROWTH_BACKEND_KEYS: Partial<Record<CoolCreditGrowthSectionId, string>> = {
  [CoolCreditGrowthSectionId.STAT_CARDS]: 'co_ga_growth_revenue_counters',
  [CoolCreditGrowthSectionId.KEY_INSIGHTS]: 'co_ga_growth_key_insights',
  [CoolCreditGrowthSectionId.DROPOFF_COMPARISON]: 'co_ga_journey_dropoff_comparison',
  [CoolCreditGrowthSectionId.ONBOARDING_FUNNEL]: 'co_ga_onboarding_funnel',
  [CoolCreditGrowthSectionId.REVENUE_CONTRIBUTION]: 'co_ga_revenue_by_plan',
  [CoolCreditGrowthSectionId.CBC_EFFECT]: 'co_ga_cbc_fetch_outcomes',
  [CoolCreditGrowthSectionId.PAYMENT_METHOD]: 'co_ga_payment_method_distribution',
  [CoolCreditGrowthSectionId.SIMULATOR_USAGE]: 'co_ga_simulator_usage_distribution',
  [CoolCreditGrowthSectionId.MONTHLY_DISPUTES_CALLS]: 'co_ga_disputes_calls_trend',
  [CoolCreditGrowthSectionId.CALL_SOURCE]: 'co_ga_call_source_distribution',
  [CoolCreditGrowthSectionId.PLAN_LIFECYCLE]: 'co_ga_plan_lifecycle_status',
  [CoolCreditGrowthSectionId.PAGE_SESSION]: 'co_ga_page_session_distribution',
};

function registerWidgetTitles(
  map: Map<string, string>,
  cardConfig: DashboardCardConfig<string>[],
  backendKeys: Partial<Record<string, string>>,
): void {
  const titleBySectionId = new Map(cardConfig.map((c) => [c.id, c.title]));
  for (const [sectionId, backendKey] of Object.entries(backendKeys)) {
    const title = titleBySectionId.get(sectionId);
    if (!title || !backendKey) continue;
    map.set(sectionId, title);
    map.set(backendKey, title);
  }
  for (const card of cardConfig) {
    map.set(card.id, card.title);
  }
}

const WIDGET_KEY_TO_DISPLAY_TITLE: ReadonlyMap<string, string> = (() => {
  const map = new Map<string, string>();
  registerWidgetTitles(map, LENDEE_DASHBOARD_CARD_CONFIG, LENDEE_DASHBOARD_BACKEND_KEYS);
  registerWidgetTitles(map, LENDEE_ANALYTICS_CARD_CONFIG, LENDEE_ANALYTICS_BACKEND_KEYS);
  registerWidgetTitles(map, COOL_CREDIT_DASHBOARD_CARD_CONFIG, COOL_CREDIT_DASHBOARD_BACKEND_KEYS);
  registerWidgetTitles(map, COOL_CREDIT_GROWTH_CARD_CONFIG, COOL_CREDIT_GROWTH_BACKEND_KEYS);
  registerWidgetTitles(map, DAILY_CRON_CARD_CONFIG, DAILY_CRON_BACKEND_KEYS);
  registerWidgetTitles(map, OVERDUE_CRON_CARD_CONFIG, OVERDUE_CRON_BACKEND_KEYS);
  registerWidgetTitles(
    map,
    ARRANGEMENT_CRON_OVERVIEW_CARD_CONFIG,
    ARRANGEMENT_CRON_OVERVIEW_BACKEND_KEYS,
  );
  registerWidgetTitles(
    map,
    ARRANGEMENT_CRON_TRENDS_CARD_CONFIG,
    ARRANGEMENT_CRON_TRENDS_BACKEND_KEYS,
  );
  registerWidgetTitles(
    map,
    ARRANGEMENT_CRON_ADMIN_PERFORMANCE_CARD_CONFIG,
    ARRANGEMENT_CRON_ADMIN_PERFORMANCE_BACKEND_KEYS,
  );
  return map;
})();

/**
 * Resolves a catalog `widget_key` to the same section title shown on the
 * source report page (e.g. `le_ln_payment_plans` → "Payment Plans").
 */
export function resolveDashboardWidgetDisplayTitle(
  widgetKey: string,
  fallback?: string,
): string {
  if (!widgetKey) return fallback ?? '';

  const direct = WIDGET_KEY_TO_DISPLAY_TITLE.get(widgetKey);
  if (direct != null) return direct;

  const stripped = stripProductPrefix(widgetKey);
  const fromStripped = WIDGET_KEY_TO_DISPLAY_TITLE.get(stripped);
  if (fromStripped != null) return fromStripped;

  for (const [canonicalKey, title] of WIDGET_KEY_TO_DISPLAY_TITLE) {
    const suffix = '_' + canonicalKey;
    if (
      widgetKey.endsWith(suffix) ||
      stripped.endsWith(suffix) ||
      widgetKey === canonicalKey ||
      stripped === canonicalKey
    ) {
      return title;
    }
  }

  return fallback ?? widgetKey;
}

/**
 * Resolves a backend `widget_key` (with or without a 2-char product prefix)
 * to a frontend section id via an exact / stripped / suffix lookup map.
 * Mirrors {@link CustomDashboardPinService}'s `endsWith('_' + key)` rule.
 */
export function resolveWidgetKeyToSectionId(
  widgetKey: string,
  widgetKeyToSectionId: ReadonlyMap<string, string>,
): string | null {
  if (!widgetKey) return null;

  const direct = widgetKeyToSectionId.get(widgetKey);
  if (direct != null) return direct;

  const stripped = stripProductPrefix(widgetKey);
  const fromStripped = widgetKeyToSectionId.get(stripped);
  if (fromStripped != null) return fromStripped;

  for (const [canonicalKey, sectionId] of widgetKeyToSectionId) {
    const suffix = '_' + canonicalKey;
    if (
      widgetKey.endsWith(suffix) ||
      stripped.endsWith(suffix) ||
      widgetKey === canonicalKey ||
      stripped === canonicalKey
    ) {
      return sectionId;
    }
  }

  return null;
}

/** Like {@link createEmbeddedSectionFilter}, but resolves backend `widget_key`s via a map. */
export function createEmbeddedSectionFilterByWidgetKeyMap<T extends { id: string }>(
  orderedVisibleSections: Signal<T[]>,
  mixedMode: Signal<boolean>,
  visibleWidgetKeys: Signal<string[] | null | undefined>,
  widgetKeyToSectionId: ReadonlyMap<string, string>,
  orderedSections?: Signal<T[]>,
): Signal<T[]> {
  return computed(() => {
    if (!mixedMode()) return orderedVisibleSections();

    const keys = visibleWidgetKeys();
    if (!keys?.length) return orderedVisibleSections();

    const allowedSectionIds = new Set<string>();
    for (const k of keys) {
      if (!k) continue;
      const mapped = resolveWidgetKeyToSectionId(k, widgetKeyToSectionId);
      if (mapped != null) {
        allowedSectionIds.add(mapped);
        continue;
      }
      allowedSectionIds.add(k);
      const stripped = stripProductPrefix(k);
      if (stripped !== k) allowedSectionIds.add(stripped);
    }

    const all = orderedSections ? orderedSections() : orderedVisibleSections();
    const filtered = all.filter((s) => allowedSectionIds.has(s.id));
    // In mixedMode, return the filtered result directly (even if empty).
    // See createEmbeddedSectionFilter for the full rationale.
    return filtered;
  });
}
